
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from '@emotion/styled';

// Interfaces TypeScript
interface UploadedImage {
  publicUrl: string;
  width?: number;
  height?: number;
}

interface Editor {
  addImageLayer: (data: any) => Promise<void>;
}

interface ImageUploaderProps {
  editor: Editor | null;
  onImageUpload: (file: File) => Promise<UploadedImage>;
}

const DropzoneContainer = styled.div<{ isDragActive: boolean }>`
  border: 2px dashed ${props => props.isDragActive ? '#3B82F6' : '#D1D5DB'};
  background-color: ${props => props.isDragActive ? '#EFF6FF' : '#FAFAFA'};
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: 8px;
  
  &:hover {
    border-color: #9CA3AF;
    background-color: #F9FAFB;
  }
`;

const UploadIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 0.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
`;

const UploadText = styled.p`
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
  font-weight: 500;
`;

const UploadSubText = styled.p`
  font-size: 0.75rem;
  color: #9CA3AF;
  margin: 0.25rem 0 0 0;
`;

const LidoImageUploader: React.FC<ImageUploaderProps> = ({ editor, onImageUpload }) => {
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    for (const file of acceptedFiles) {
      try {
        // Upload para Supabase primeiro
        const uploadedImage = await onImageUpload(file);
        
        if (editor && uploadedImage) {
          await editor.addImageLayer({
            image: {
              url: uploadedImage.publicUrl,
              naturalWidth: uploadedImage.width || 0,
              naturalHeight: uploadedImage.height || 0
            },
            position: { x: 100, y: 100 },
            size: { width: 200, height: 200 }
          });
        }
      } catch (error) {
        console.error('Erro ao fazer upload da imagem:', error);
      }
    }
  }, [editor, onImageUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp']
    },
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  return (
    <DropzoneContainer {...getRootProps()} isDragActive={isDragActive}>
      <input {...getInputProps()} />
      <UploadIcon>
        📁
      </UploadIcon>
      <UploadText>
        {isDragActive
          ? 'Solte as imagens aqui...'
          : 'Arraste imagens ou clique para selecionar'}
      </UploadText>
      <UploadSubText>
        PNG, JPG, JPEG, SVG, WEBP (máx. 10MB)
      </UploadSubText>
    </DropzoneContainer>
  );
};

export default LidoImageUploader;
